import React, { useEffect, useState } from 'react';
import { Trophy, Sparkles, X } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { UnlockType } from '../types';

interface UnlockCelebrationProps {
  unlockName: string;
  unlockDescription?: string;
  unlockType: UnlockType;
  icon?: string;
  color?: string;
  onClose: () => void;
}

export function UnlockCelebration({
  unlockName,
  unlockDescription,
  unlockType,
  icon,
  color = '#3b82f6',
  onClose
}: UnlockCelebrationProps) {
  const [isVisible, setIsVisible] = useState(false);

  // ⚠️ Cleared on unmount: a card closed inside these 100ms must not set state after
  // it is gone (in a test file whose environment is already torn down, React threw).
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const getIcon = () => {
    if (icon && icon in LucideIcons) {
      const IconComponent = LucideIcons[icon as keyof typeof LucideIcons] as React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
      return <IconComponent className="w-16 h-16" style={{ color }} />;
    }
    return <Trophy className="w-16 h-16" style={{ color }} />;
  };

  const getTypeLabel = () => {
    switch (unlockType) {
      case 'domain':
        return 'New Domain Unlocked!';
      case 'feature':
        return 'New Feature Unlocked!';
      case 'breathwork_style':
        return 'New Breathwork Style Unlocked!';
      case 'discipline':
        return 'New Discipline Unlocked!';
      default:
        return 'Unlocked!';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] animate-in fade-in">
      <div
        className={`bg-card rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transition-all duration-500 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
        }`}
      >
        <div
          className="relative p-8 text-center"
          style={{
            background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative inline-block mb-4">
            <div
              className="absolute inset-0 rounded-full animate-ping"
              style={{
                backgroundColor: color,
                opacity: 0.3
              }}
            />
            <div
              className="relative p-6 rounded-full"
              style={{
                backgroundColor: `${color}20`
              }}
            >
              {getIcon()}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-5 h-5" style={{ color }} />
            <h2
              className="text-2xl font-bold"
              style={{ color }}
            >
              {getTypeLabel()}
            </h2>
            <Sparkles className="w-5 h-5" style={{ color }} />
          </div>

          <h3 className="text-xl font-semibold text-foreground mb-2">
            {unlockName}
          </h3>

          <p className="text-sm text-muted-foreground mb-6">
            {unlockDescription}
          </p>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-lg font-medium text-white transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: color
              }}
            >
              Awesome!
            </button>
          </div>
        </div>

        <div className="relative h-2 overflow-hidden">
          <div
            className="absolute inset-0 animate-pulse"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)`,
              animation: 'shimmer 2s infinite'
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}
